// ข้อมูลคนหนึ่งแถวในแผง "ผู้เข้าถึง" — แปลงร่างและจัดเรียง
//
// แยกจาก component เพราะเป็น logic ล้วน ๆ ไม่มี JSX จึงรันตรวจได้โดยไม่ต้องมี DOM
import type { Employee } from "@features/auth/api/auth-api";
import type { BrandMember } from "../api/settings-api";

/**
 * แถวผลค้นหาได้มาจากรายชื่อพนักงานครบทุก field แต่แถวผู้เข้าถึงที่บันทึกไว้
 * เก็บแค่รหัส+ชื่อในฐานข้อมูลเรา — ที่เหลือจึงต้องไปหยิบจากรายชื่อพนักงานด้วยรหัส
 */
export type PersonView = {
  empCode: string;
  thai: string;
  eng: string;
  nick: string;
  position: string;
  department: string;
  photo?: string;
};

export function fromEmployee(p: Employee): PersonView {
  return {
    empCode: p.empCode,
    thai: p.empThaiName?.trim() || "",
    eng: p.empEngName?.trim() || "",
    nick: p.nickName?.trim() || "",
    position: p.positionName?.trim() || "",
    department: p.departmentName?.trim() || "",
    photo: p.profileURL || undefined,
  };
}

/**
 * คนที่ลาออกไปแล้วจะไม่อยู่ในรายชื่อพนักงานอีก และตอนรายชื่อยังโหลดไม่เสร็จก็หาไม่เจอ
 * ทั้งสองกรณีต้องยังเห็นชื่อที่บันทึกไว้ ไม่ใช่แถวเปล่า
 */
export function fromMember(m: BrandMember, staff?: Employee): PersonView {
  if (staff) return fromEmployee(staff);
  return {
    empCode: m.empCode,
    thai: m.empName?.trim() || "",
    eng: "",
    nick: "",
    position: "",
    department: "",
  };
}

/** ตัวอักษรในวงกลมตอนไม่มีรูป — เอาอักษรละตินก่อนเพราะอ่านง่ายกว่าในวงเล็ก */
export function initialOf(v: PersonView): string {
  return (v.nick || v.eng || v.thai || "?").charAt(0).toUpperCase();
}

/** ชื่อที่ใช้แสดงเป็นหลัก — ตัวเรียงต้องใช้ตัวเดียวกัน ไม่งั้นลำดับบนจอจะดูมั่ว */
export function primaryName(v: PersonView): string {
  return v.thai || v.eng || v.empCode;
}

/**
 * เรียงตามแผนก แล้วค่อยเรียงคนในแผนก
 *
 * ใช้ locale ไทยเหมือนที่อื่นในระบบ เพราะเรียงแบบ default จะได้ลำดับพยัญชนะไทยผิด
 * คนที่ไม่รู้แผนก (ลาออกแล้ว หรือรายชื่อพนักงานยังโหลดไม่เสร็จ) ไปต่อท้ายสุด
 * ไม่ใช่ลอยขึ้นหัวรายการใต้แผนกชื่อว่าง
 */
export function byDepartmentThenName(a: PersonView, b: PersonView): number {
  // มีแผนกกับไม่มีแผนกเทียบกัน — ตัดสินก่อนเลย ไม่ต้องไปเทียบชื่อแผนกที่เป็นค่าว่าง
  if (!a.department !== !b.department) return a.department ? -1 : 1;
  const dept = a.department.localeCompare(b.department, "th");
  if (dept !== 0) return dept;
  return primaryName(a).localeCompare(primaryName(b), "th");
}
